/**
 * DeductionRiderToHeadPage Component
 * 라이더 → 본사 일차감 페이지
 *
 * 본사가 라이더에게 부과하는 일차감 관리
 * - 조회: DEDUCT_TARGET='R' AND DEDUCT_REG_TYPE='C'
 * - 등록: DEDUCT_TARGET='R', DEDUCT_REG_TYPE='C'
 */
import React, { useEffect, useState, useCallback } from "react";
import { deductionApi, DeductionDto, riderApi, RiderInfoDto } from "@/shared/api/deductionApi";
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { usePagination } from "@/shared/hooks/usePagination";
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { getItem } from "@/shared/utils/storage";
import { supplierApi } from "@/features/partner/supplierStatus/api/supplierApi";
import { Supplier } from "@/features/partner/supplierStatus/types/supplier.types";
import { commonCodeApi } from "@/shared/api/commonCodeApi";
import { normalizeDate, calcEndDate, calcDays } from '../utils/riderDeductionHelpers';
import RiderToHeadTable from './RiderToHeadTable';
import { NewDeductionForm } from './RiderToHeadAddRow';
import '@/shared/styles/common-table.css';
import '@/shared/styles/search-form.css';
import '../styles/DeductionRiderToHeadPage.css';

const ITEMS_PER_PAGE = 10;

const EMPTY_FORM: NewDeductionForm = {
    suppId: '',
    riderId: '',
    deductionTypeCode: '',
    deductionName: '',
    deductionAmount: '',
    startDate: '',
    endDate: '',
    deductDays: '',
};

const DeductionRiderToHeadPage: React.FC = () => {
    const { showToast } = useToast();

    const [loading, setLoading] = useState(false);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [deductionTypesLoading, setDeductionTypesLoading] = useState(false);

    const [deductions, setDeductions] = useState<DeductionDto[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [deductionTypes, setDeductionTypes] = useState<Array<{ code: string; name: string }>>([]);

    const [filterSuppId, setFilterSuppId] = useState<string>('');
    const [newDeduction, setNewDeduction] = useState<NewDeductionForm>(EMPTY_FORM);
    const [formRiders, setFormRiders] = useState<RiderInfoDto[]>([]);
    const [formRidersLoading, setFormRidersLoading] = useState(false);

    const { currentPage, totalPages, currentItems, goToPage, resetPagination } =
        usePagination({ items: deductions, itemsPerPage: ITEMS_PER_PAGE });

    const fetchDeductionTypes = useCallback(async () => {
        try {
            setDeductionTypesLoading(true);
            const response = await commonCodeApi.getCommonCodeList('FEE_DEDUCT');
            const types = response.codes
                .filter(code => code.useYn === 'Y')
                .map(code => ({ code: code.comCode, name: code.comCdName }));
            setDeductionTypes(types);
        } catch {
            showToast('공제 타입 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setDeductionTypesLoading(false);
        }
    }, [showToast]);

    const fetchSuppliers = useCallback(async () => {
        try {
            setSuppliersLoading(true);
            const response = await supplierApi.getSupplierList();
            setSuppliers(response.suppliers.map(s => ({
                id: s.suppId,
                name: s.suppName || s.suppId,
                status: ''
            })));
        } catch {
            showToast('협력사 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setSuppliersLoading(false);
        }
    }, [showToast]);

    const fetchFormRiders = useCallback(async (suppId: string) => {
        if (!suppId) { setFormRiders([]); return; }
        try {
            setFormRidersLoading(true);
            const response = await riderApi.getRiderList(suppId);
            setFormRiders(response.riders || []);
        } catch {
            showToast('라이더 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setFormRiders([]);
        } finally {
            setFormRidersLoading(false);
        }
    }, [showToast]);

    const fetchDeductions = useCallback(async (suppId?: string) => {
        try {
            setLoading(true);
            const response = await deductionApi.getDeductionList({
                suppId: suppId || undefined,
                deductTarget: 'R'
            });
            const filtered = (response.deducts || [])
                .filter(d => d.deductTarget === 'R' && d.deductRegType === 'C')
                .sort((a, b) => b.seqNo - a.seqNo);
            setDeductions(filtered);
        } catch {
            showToast('일차감 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setDeductions([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchDeductionTypes();
        fetchSuppliers();
        fetchDeductions();
    }, [fetchDeductionTypes, fetchSuppliers, fetchDeductions]);

    const handleSearch = () => {
        fetchDeductions(filterSuppId);
        resetPagination();
    };

    const handleDeleteDeduction = async (seqNo: number) => {
        if (!window.confirm('해당 일차감을 삭제(비활성화)하시겠습니까?')) return;
        try {
            const userId = getItem<string>('USER_ID') || '';
            await deductionApi.toggleUseYn({ seqNos: [seqNo], useYn: 'N', updUser: userId });
            setDeductions(prev => prev.filter(d => d.seqNo !== seqNo));
            showToast('일차감이 삭제되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('일차감 삭제에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    const handleFormSuppIdChange = (suppId: string) => {
        setNewDeduction(prev => ({ ...prev, suppId, riderId: '' }));
        fetchFormRiders(suppId);
    };

    const handleStartDateChange = (value: string) => {
        const normalized = normalizeDate(value);
        const days = parseInt(newDeduction.deductDays, 10);
        if (normalized && days > 0) {
            setNewDeduction(prev => ({ ...prev, startDate: normalized, endDate: calcEndDate(normalized, days) }));
        } else if (normalized && newDeduction.endDate) {
            const d = calcDays(normalized, newDeduction.endDate);
            setNewDeduction(prev => ({ ...prev, startDate: normalized, deductDays: d > 0 ? String(d) : '' }));
        } else {
            setNewDeduction(prev => ({ ...prev, startDate: normalized }));
        }
    };

    const handleEndDateChange = (value: string) => {
        const normalized = normalizeDate(value);
        if (normalized && newDeduction.startDate) {
            const d = calcDays(newDeduction.startDate, normalized);
            setNewDeduction(prev => ({ ...prev, endDate: normalized, deductDays: d > 0 ? String(d) : '' }));
        } else {
            setNewDeduction(prev => ({ ...prev, endDate: normalized }));
        }
    };

    const handleDeductDaysChange = (value: string) => {
        const days = parseInt(value, 10);
        if (newDeduction.startDate && days > 0) {
            setNewDeduction(prev => ({ ...prev, deductDays: value, endDate: calcEndDate(newDeduction.startDate, days) }));
        } else {
            setNewDeduction(prev => ({ ...prev, deductDays: value }));
        }
    };

    const handleAddDeduction = async () => {
        if (!newDeduction.suppId) { showToast('협력사를 선택해주세요.', { type: ToastType.WARNING }); return; }
        if (!newDeduction.riderId) { showToast('라이더를 선택해주세요.', { type: ToastType.WARNING }); return; }
        if (!newDeduction.deductionTypeCode) { showToast('공제타입을 선택해주세요.', { type: ToastType.WARNING }); return; }
        if (!newDeduction.deductionName) { showToast('공제명을 입력해주세요.', { type: ToastType.WARNING }); return; }
        if (!newDeduction.deductionAmount) { showToast('공제금액을 입력해주세요.', { type: ToastType.WARNING }); return; }
        if (!newDeduction.startDate) { showToast('시작일자를 입력해주세요.', { type: ToastType.WARNING }); return; }
        if (!newDeduction.endDate) { showToast('종료일자를 입력해주세요.', { type: ToastType.WARNING }); return; }
        if (newDeduction.endDate < newDeduction.startDate) {
            showToast('종료일자는 시작일자 이후여야 합니다.', { type: ToastType.WARNING });
            return;
        }

        try {
            const userId = getItem<string>('USER_ID') || '';
            const deductCnt = calcDays(newDeduction.startDate, newDeduction.endDate);
            const response = await deductionApi.registerDeduction({
                deductTarget: 'R',
                deductRegType: 'C',
                suppId: newDeduction.suppId,
                riderId: newDeduction.riderId,
                deductType: newDeduction.deductionTypeCode,
                calcType: '1',
                deductName: newDeduction.deductionName,
                deductCnt: deductCnt > 0 ? deductCnt : null,
                deductAmt: parseInt(newDeduction.deductionAmount, 10),
                deductDispYn: 'Y',
                startDay: newDeduction.startDate,
                endDay: newDeduction.endDate,
                insUser: userId
            });

            showToast('일차감이 등록되었습니다.', { type: ToastType.SUCCESS });

            const newDeductionData: DeductionDto = {
                seqNo: response.seqNo || Date.now(),
                deductTarget: 'R',
                deductRegType: 'C',
                suppId: newDeduction.suppId,
                riderId: newDeduction.riderId,
                deductType: newDeduction.deductionTypeCode,
                calcType: '1',
                deductName: newDeduction.deductionName,
                deductCnt: deductCnt > 0 ? deductCnt : null,
                deductAmt: parseInt(newDeduction.deductionAmount, 10),
                deductRate: null,
                deductDispYn: 'Y',
                startDay: newDeduction.startDate,
                endDay: newDeduction.endDate,
                insDate: new Date().toISOString(),
                insUser: userId,
                updDate: null,
                updUser: null
            };

            setDeductions(prev => [newDeductionData, ...prev]);
            setNewDeduction(EMPTY_FORM);
            setFormRiders([]);
            resetPagination();
        } catch {
            showToast('일차감 등록에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    const supplierOptions: SelectOption[] = suppliers.map(s => ({ value: s.id, label: s.name }));
    const formRiderOptions: SelectOption[] = formRiders.map(r => ({
        value: r.riderId,
        label: r.riderName ? `${r.riderId} (${r.riderName})` : r.riderId
    }));

    const previewDays = newDeduction.startDate && newDeduction.endDate
        ? calcDays(newDeduction.startDate, newDeduction.endDate)
        : 0;
    const previewTotal = previewDays > 0 && newDeduction.deductionAmount
        ? previewDays * parseInt(newDeduction.deductionAmount, 10)
        : 0;

    if (loading && deductions.length === 0) {
        return (
            <div className="supplier-selection-page">
                <div className="page-title-wrapper">
                    <h1 className="page-title">라이더 → 본사 일차감</h1>
                </div>
                <Loading message="일차감 내역을 불러오는 중..." />
            </div>
        );
    }

    return (
        <div className="supplier-selection-page">
            <div className="page-title-wrapper">
                <h1 className="page-title">라이더 → 본사 일차감</h1>
            </div>

            {/* 필터 섹션 */}
            <div className="card search-card">
                <div className="card-body">
                    <div className="search-form rider-to-head-search-form">
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={supplierOptions}
                                    value={filterSuppId}
                                    onChange={setFilterSuppId}
                                    placeholder={suppliersLoading ? '로딩 중...' : '협력사 선택'}
                                    disabled={suppliersLoading}
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>
                        <div className="form-group button-group">
                            <button
                                className="btn btn-primary"
                                onClick={handleSearch}
                                disabled={loading}
                            >
                                <i className="fas fa-search" />
                                조회
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 테이블 섹션 */}
            <RiderToHeadTable
                loading={loading}
                currentItems={currentItems}
                currentPage={currentPage}
                deductions={deductions}
                suppliers={suppliers}
                deductionTypes={deductionTypes}
                newDeduction={newDeduction}
                supplierOptions={supplierOptions}
                formRiderOptions={formRiderOptions}
                deductionTypesLoading={deductionTypesLoading}
                suppliersLoading={suppliersLoading}
                formRidersLoading={formRidersLoading}
                previewDays={previewDays}
                previewTotal={previewTotal}
                onDelete={handleDeleteDeduction}
                onFormSuppIdChange={handleFormSuppIdChange}
                onRiderChange={(riderId) => setNewDeduction(prev => ({ ...prev, riderId }))}
                onDeductionTypeChange={(code, name) => setNewDeduction(prev => ({ ...prev, deductionTypeCode: code, deductionName: name }))}
                onDeductionNameChange={(name) => setNewDeduction(prev => ({ ...prev, deductionName: name }))}
                onDeductionAmountChange={(amount) => setNewDeduction(prev => ({ ...prev, deductionAmount: amount }))}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                onDeductDaysChange={handleDeductDaysChange}
                onAddDeduction={handleAddDeduction}
            />

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default DeductionRiderToHeadPage;
